import { EventType } from "@/analytics/analytics-events";
import type { ToggleOverallVisibilityProperties } from "@/analytics/staging-area-workflows-events-properties";
import { HorizontalResizeContainer } from "@/components/ui/resize-container";
import { Features, selectHasFeature } from "@/store/features/features-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { APP_VERSION } from "@/utils/env-constants";
import {
  CollapseListIcon,
  FaroIconButton,
  FaroIconButtonSizes,
  FaroText,
  FaroTooltip,
  grey,
  NO_TRANSLATE_CLASS,
  NonVisibleIcon,
  NoTranslate,
  TruncatedFaroText,
  useBreakpointMdUp,
  VisibleIcon,
} from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { selectProjectName } from "@faro-lotv/project-source";
import type { CaptureTreeEntityRevision } from "@faro-lotv/service-wires";
import { Box, Divider, Stack, Typography } from "@mui/material";
import { memo, useMemo, useRef, useState, type JSX } from "react";
import type { TreeApi } from "react-arborist";
import { selectValidRevisionEntities } from "../store/data-preparation-ui/data-preparation-ui-selectors";
import { setVisibilityForEntities } from "../store/data-preparation-ui/data-preparation-ui-slice";
import { selectRootVisibility } from "../store/data-preparation-ui/data-preparation-ui-visibility-selectors";
import { selectRootEntity } from "../store/revision/revision-entity-selectors";
import { sortByName } from "../utils/sorting";
import { ActionsPanel } from "./action-panel/action-panel";
import { ImportCapturesButton } from "./import-scans-button";
import { ScanTree } from "./scan-tree/scan-tree";
import { ScanTreeContextMenu } from "./scan-tree/scan-tree-context-menu";

/** @returns Sidebar to use throughout the data preparation tool. */
export const DataPreparationSidebar = memo(
  function DataPreparationSidebar(): JSX.Element | null {
    const isMdUp = useBreakpointMdUp();
    // On small screens, the sidebar is shown in the drawer of the header instead.
    if (!isMdUp) return null;

    return (
      <HorizontalResizeContainer
        initialWidth={320}
        minWidth={200}
        maxWidth={500}
        handleSide="right"
      >
        <DataPreparationSidebarBase />
      </HorizontalResizeContainer>
    );
  },
);

/** @returns the sidebar for the data preparation page, without the resize handle. */
export function DataPreparationSidebarBase(): JSX.Element {
  const dispatch = useAppDispatch();
  const isSimpleScanOrganization = useAppSelector(
    selectHasFeature(Features.SimpleScanOrganization),
  );

  const projectName = useAppSelector(selectProjectName) ?? "Project";
  const entities = useAppSelector(selectValidRevisionEntities);
  const rootEntity = useAppSelector(selectRootEntity);

  const allNodesVisible = useAppSelector(selectRootVisibility);

  const [rootVisibilityHovered, setRootVisibilityHovered] = useState(false);
  const [isTreeFullyCollapsed, setIsTreeFullyCollapsed] = useState(true);
  const treeRef = useRef<TreeApi<CaptureTreeEntityRevision>>(null);

  const showTreeLevelButtons = rootVisibilityHovered || !allNodesVisible;
  // This buttons name are as per functionality,
  // i.e. when a VisibleIcon is shown and on clciking it all nodes will be hidden,
  // so that button name would be 'Hide all' and vice versa
  const hideAllNodesButton = allNodesVisible && rootVisibilityHovered;
  const showAllNodesButton = !allNodesVisible;

  const visibleButtonTooltip = hideAllNodesButton ? "Hide all" : "Show all";

  /** Sorts ScanTree entries alphabetically and case insensitive, to prevent scan tree entities to appear out of order */
  const sortedEntities = useMemo(
    () => [...entities].sort(sortByName),
    [entities],
  );

  /** ids of the valid revision entities */
  const validEntityIds = sortedEntities.map((entity) => entity.id);

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* project  Header */}
      <Box
        component="div"
        sx={{
          pt: 2,
          px: 2,
          width: "100%",
        }}
      >
        <Stack
          sx={{
            gap: 1.5,
            width: "100%",
          }}
        >
          <Stack
            sx={{
              gap: 0.75,
              width: "100%",
            }}
          >
            <NoTranslate>
              <TruncatedFaroText variant="heading14">
                {projectName}
              </TruncatedFaroText>
            </NoTranslate>
            <Divider />
          </Stack>

          <ActionsPanel entities={sortedEntities} />
        </Stack>
      </Box>

      {/* project details */}
      <Stack
        sx={{
          flex: 1,
          overflowY: "hidden",
          pl: 2,

          "& [role=tree] [role=treeitem]:focus-visible": {
            // Disable default styling for focus
            outline: "none",
            // Ensure there is enough space to see the box shadow
            px: "3px",
            // Avoid the background of other selections overlapping the focus highlighting
            zIndex: 1,
          },

          "& [role=tree] [role=treeitem]:focus-visible > div > div": {
            // Custom focus highlighting, to align with the selection
            boxShadow: `0 0 0 1px ${grey[0]}, 0 0 0 3px ${grey[1000]}`,
          },

          "& [role=tree] [role=treeitem]:focus-visible > div > div > div": {
            // Avoid layout shift of text due to box shadow
            m: "-3px",
          },
        }}
        aria-label="capture tree"
      >
        <Stack
          direction="row"
          sx={{
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
            py: 0.5,
            // This part isn't scrolling, so align the right-side padding with the other items
            pr: 1.125,
          }}
          onPointerEnter={() => setRootVisibilityHovered(true)}
          onPointerLeave={() => setRootVisibilityHovered(false)}
        >
          <FaroText variant="bodyM" lineHeight={1.75} aria-label="capture-tree">
            Captures
          </FaroText>
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 1,
              justifyContent: "space-between",
              minWidth: 0,
            }}
          >
            {showTreeLevelButtons && (
              <>
                {!isTreeFullyCollapsed && (
                  <FaroTooltip title="Collapse all">
                    <FaroIconButton
                      size={FaroIconButtonSizes.xs}
                      aria-label="collapse all"
                      onClick={() => {
                        Analytics.track(EventType.collapseScanTree);
                        treeRef.current?.closeAll();
                      }}
                    >
                      <CollapseListIcon />
                    </FaroIconButton>
                  </FaroTooltip>
                )}
                <FaroTooltip title={visibleButtonTooltip}>
                  <FaroIconButton
                    size={FaroIconButtonSizes.xs}
                    aria-label="overall-visibility"
                    onClick={() => {
                      Analytics.track<ToggleOverallVisibilityProperties>(
                        EventType.toggleOverallVisibility,
                        {
                          value: visibleButtonTooltip,
                        },
                      );
                      dispatch(
                        setVisibilityForEntities({
                          entityIds: validEntityIds,
                          isVisible: !allNodesVisible,
                        }),
                      );
                    }}
                  >
                    <>
                      {hideAllNodesButton && <VisibleIcon />}
                      {showAllNodesButton && <NonVisibleIcon />}
                    </>
                  </FaroIconButton>
                </FaroTooltip>
                {isSimpleScanOrganization ? (
                  <ScanTreeContextMenu
                    entity={rootEntity}
                    onCloseContextMenu={() => setRootVisibilityHovered(false)}
                  />
                ) : null}
              </>
            )}
          </Stack>
        </Stack>

        <ScanTree
          ref={treeRef}
          entities={sortedEntities}
          onToggle={() => {
            const hasExpandedNode = Object.values(
              treeRef.current?.openState ?? {},
            ).some((isOpen) => isOpen);

            setIsTreeFullyCollapsed(!hasExpandedNode);
          }}
        />
      </Stack>

      <ImportCapturesButton
        sx={{
          mx: 2,
          my: 1,
        }}
      />

      {/* copyright footer */}
      <Typography
        variant="caption"
        className={NO_TRANSLATE_CLASS}
        sx={{
          width: "100%",
          color: grey[400],
          ml: 0.8,
          // Make sure that long dev-only version strings don't break design
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        ©FARO v{APP_VERSION}
      </Typography>
    </Box>
  );
}
